import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/** a line of the process log, with the time it arrived and its kind (error / end / info) */
export interface ProcessLogLine {
  id: number;
  time: string;
  html: string;
  text: string;
  level: 'error' | 'end' | 'start' | 'info';
}

const ERROR_WORDS = /\b(error|erreur|exception|failed|echec|\u00e9chec|fout)\b|@error@/i;
const START_WORDS = /\b(lancement|launch|launching|start|starting|d\u00e9marrage|demarrage|gestart)\b/i;
const END_WORDS = /\b(termin|finished|completed|voltooid|success|succ[e\u00e8]s)/i;

/**
 * Log of a running process: one row per line with its time, errors and the end line marked, a filter for long
 * logs, copy and download, and an automatic scroll that stops while the user reads above (a button then brings
 * the new lines).
 */
@Component({
  selector: 'iu-process-logs-ui',
  templateUrl: './process-logs-ui.component.html',
  styleUrls: ['./process-logs-ui.component.scss'],
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class ProcessLogsUiComponent {
  logsContainerElt = viewChild('logsContainer', { read: ElementRef });
  /** title shown at the start of the tool row (one header line instead of a title above the tools) */
  readonly heading = input<string | null>(null);
  /** a close button at the end of the tool row */
  readonly closable = input(false);
  readonly closed = output<void>();

  readonly lines = signal<ProcessLogLine[]>([]);
  /** raw lines (kept for the callers that read them) */
  readonly logs = computed(() => this.lines().map((l) => l.html));
  readonly filter = signal('');
  readonly shown = computed(() => {
    const q = this.filter().trim().toLowerCase();
    return q ? this.lines().filter((l) => l.text.toLowerCase().includes(q)) : this.lines();
  });
  readonly errorCount = computed(() => this.lines().filter((l) => l.level === 'error').length);
  /** lines arrived while the user reads above: "new lines" button */
  readonly newBelow = signal(0);
  readonly copied = signal(false);
  #nextId = 1;
  #follow = true;
  readonly #zone = inject(NgZone);

  constructor() {
    // scroll listener outside Angular: only clearing the "new lines" counter enters the zone
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      const el = this.logsContainerElt()?.nativeElement as HTMLElement | undefined;
      if (!el) return;
      const listener = (event: Event) => this.onScroll(event);
      this.#zone.runOutsideAngular(() => el.addEventListener('scroll', listener, { passive: true }));
      destroyRef.onDestroy(() => el.removeEventListener('scroll', listener));
    });
  }

  addLog(...logs: string[]) {
    const now = new Date();
    const time = [now.getHours(), now.getMinutes(), now.getSeconds()].map((n) => String(n).padStart(2, '0')).join(':');
    const added = logs
      .filter((l) => l !== undefined && l !== null && String(l).trim() !== '')
      .map((l) => {
        const html = String(l);
        const text = this.#toText(html);
        const level: ProcessLogLine['level'] = ERROR_WORDS.test(text)
          ? 'error'
          : END_WORDS.test(text)
            ? 'end'
            : START_WORDS.test(text)
              ? 'start'
              : 'info';
        return { id: this.#nextId++, time, html, text, level };
      });
    if (!added.length) return;
    this.lines.update((previous) => [...previous, ...added]);
    if (this.#follow) {
      setTimeout(() => this.goToBottom(), 30);
    } else {
      this.newBelow.update((n) => n + added.length);
    }
  }

  clearLogs() {
    this.lines.set([]);
    this.newBelow.set(0);
    this.filter.set('');
    this.#follow = true;
  }

  /** follows the end while the user is at the bottom; reading above stops it */
  onScroll(event: Event) {
    const el = event.target as HTMLElement;
    this.#follow = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (this.#follow && this.newBelow()) this.#zone.run(() => this.newBelow.set(0));
  }

  goToBottom() {
    const el = this.logsContainerElt()?.nativeElement as HTMLElement | undefined;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    this.#follow = true;
    this.newBelow.set(0);
  }

  copyAll() {
    const text = this.#plain();
    const done = () => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => {});
    }
  }

  download() {
    const blob = new Blob([this.#plain()], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const d = new Date();
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
    a.href = url;
    a.download = `process-log-${stamp}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  #plain(): string {
    return this.lines()
      .map((l) => `${l.time}  ${l.text}`)
      .join('\n');
  }

  /** text of an html line (tags removed, entities decoded) */
  #toText(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
  }
}
